import { useState } from "react";
import { Bell, Eye, EyeOff, Loader2, Lock, Mail, ShieldAlert, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSwitcher } from "@/components/settings/LanguageSwitcher";
import { EmailNotificationsCard } from "@/components/settings/EmailNotificationsCard";
import { cn } from "@/lib/utils";
import { hairlineClass, slateCardClass } from "@/lib/card-styles";
import { typeBodyClass, typeMetaClass, typeTitleClass } from "@/lib/typography";

interface AccountSettingsSectionProps {
  email: string;
  /** Each takes its three field values, reports its own outcome, and resolves
   *  to whether the change landed so the draft can be cleared or kept. */
  changeEmail: (newEmail: string, confirmEmail: string, password: string) => Promise<boolean>;
  changePassword: (current: string, next: string, confirm: string) => Promise<boolean>;
  isChangingEmail: boolean;
  isChangingPassword: boolean;
  notificationPermission: NotificationPermission | null;
  isRequestingPermission: boolean;
  onRequestNotificationPermission: () => void;
}

/**
 * A password input with a reveal toggle.
 *
 * This block was copy-pasted four times across the email and password forms —
 * the same `relative` wrapper, the same absolutely-positioned ghost button, the
 * same `Eye`/`EyeOff` swap, four different sets of show-state props threading
 * down from the modal. The reveal is a local concern of the field, so the state
 * lives here; nothing above needs to know.
 */
function SecretField({
  id,
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className={typeMetaClass}>
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={revealed ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="h-10 rounded-lg pr-10 text-sm"
        />
        <button
          type="button"
          aria-label={revealed ? "Hide password" : "Show password"}
          aria-pressed={revealed}
          onClick={() => setRevealed((value) => !value)}
          className={cn(
            "absolute right-0 top-0 flex h-10 w-10 items-center justify-center rounded-r-lg",
            "text-stone-400 outline-none transition-colors hover:text-stone-700",
            "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            "dark:hover:text-stone-200",
          )}
        >
          {revealed ? (
            <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}

/** Icon well + title + description at the head of a settings card. */
function CardHeading({
  icon: Icon,
  iconClassName,
  title,
  description,
}: {
  icon: typeof Bell;
  iconClassName?: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
          iconClassName ?? "bg-primary/10 text-primary",
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <h4 className="text-sm font-semibold text-stone-900 dark:text-stone-50">{title}</h4>
        <p className={cn(typeBodyClass, "mt-0.5 text-xs")}>{description}</p>
      </div>
    </div>
  );
}

/**
 * Credentials, notifications, and interface language.
 *
 * ## The form state stayed here; the handlers did not
 *
 * This pane previously took 24 props, 15 of which were setters and
 * show/hide toggles threaded down from `ProfileModal`. The draft fields are
 * genuinely local — nobody outside this component reads a half-typed password —
 * so they stay in local state. The submissions are not: each is now one call
 * that takes its three values and reports its own outcome, which is what
 * `useProfileSettings` exists for.
 *
 * ## Nothing here is a primary action
 *
 * Changing an email and changing a password are two independent maintenance
 * tasks, and neither is *the* next step, so both commit buttons stay outline.
 * The one primary CTA in this dialog is Save, on the Profile pane.
 */
export const AccountSettingsSection = ({
  email,
  changeEmail,
  changePassword,
  isChangingEmail,
  isChangingPassword,
  notificationPermission,
  isRequestingPermission,
  onRequestNotificationPermission,
}: AccountSettingsSectionProps) => {
  const { t } = useTranslation();

  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const submitEmail = async () => {
    const landed = await changeEmail(newEmail, confirmEmail, emailPassword);
    // Clearing only on success is deliberate: a rejected change keeps what was
    // typed so the learner can fix one field instead of starting over.
    if (landed) {
      setNewEmail("");
      setConfirmEmail("");
      setEmailPassword("");
    }
  };

  const submitPassword = async () => {
    const landed = await changePassword(currentPassword, nextPassword, confirmPassword);
    if (landed) {
      setCurrentPassword("");
      setNextPassword("");
      setConfirmPassword("");
    }
  };

  const notificationsBlocked = notificationPermission === "denied";
  const notificationsOff = notificationPermission !== "granted";

  return (
    <div className="space-y-6">
      <div>
        <h3 className={typeTitleClass}>{t("settings.accountPanel.title", "Account")}</h3>
        <p className={cn(typeBodyClass, "mt-1")}>
          {t("settings.subtitle", "Manage your account security and preferences")}
        </p>
      </div>

      {/* Language comes first: it is the one setting that changes what every
          label below it says, so burying it under a password form is backwards. */}
      <div className={cn(slateCardClass, "p-4 sm:p-5")}>
        <LanguageSwitcher variant="row" />
        <div className={cn("mt-3 pl-11", hairlineClass, "space-y-1 border-t pt-3")}>
          <p className="text-xs text-stone-600 dark:text-stone-400">
            {t("settings.language.learningContentNote")}
          </p>
          <p className="text-[11px] text-stone-500 dark:text-stone-500">
            {t("settings.language.hint", "Applies immediately — no reload needed.")}
          </p>
        </div>
      </div>

      {/* One context band: notifications, and only while they are off. Once
          granted the card is gone, which is the point — it should not stay on
          screen as decoration for something already true. */}
      {notificationsOff && (
        <div
          className={cn(
            "space-y-3 rounded-2xl border p-4 sm:p-5",
            notificationsBlocked
              ? "border-red-200 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/20"
              : "border-amber-200 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20",
          )}
        >
          <CardHeading
            icon={notificationsBlocked ? ShieldAlert : Bell}
            iconClassName={
              notificationsBlocked
                ? "bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-300"
                : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
            }
            title={t("settings.account.pushNotifications", "Push Notifications")}
            description={t(
              "settings.account.pushNotificationsDescription",
              "Get alerts when sessions are shared in your study groups",
            )}
          />

          <div className="flex items-center justify-between gap-3">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 text-sm font-medium",
                notificationsBlocked
                  ? "text-red-700 dark:text-red-300"
                  : "text-stone-600 dark:text-stone-400",
              )}
            >
              {notificationsBlocked ? (
                <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <Bell className="h-4 w-4 shrink-0" aria-hidden="true" />
              )}
              {notificationsBlocked
                ? t("settings.account.blockedByBrowser", "Blocked by browser")
                : t("settings.account.notEnabled", "Not enabled")}
            </span>

            <Button
              onClick={onRequestNotificationPermission}
              disabled={isRequestingPermission}
              variant={notificationsBlocked ? "outline" : "default"}
              size="sm"
              className="h-9 shrink-0 gap-1.5 rounded-lg text-xs"
            >
              {isRequestingPermission && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              )}
              {isRequestingPermission
                ? t("settings.account.requesting", "Requesting…")
                : notificationsBlocked
                  ? t("settings.account.unblock", "Unblock in Browser")
                  : t("settings.account.enable", "Enable")}
            </Button>
          </div>

          {notificationsBlocked && (
            <ol className="list-inside list-decimal space-y-1 rounded-lg border border-red-200/70 bg-white/60 p-3 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
              {(t("settings.account.howToEnableSteps", { returnObjects: true }) as string[]).map(
                (step) => (
                  <li key={step}>{step}</li>
                ),
              )}
            </ol>
          )}
        </div>
      )}

      <EmailNotificationsCard />

      <div className={cn(slateCardClass, "space-y-4 p-4 sm:p-5")}>
        <CardHeading
          icon={Mail}
          title={t("settings.account.changeEmail", "Change Email")}
          description={t(
            "settings.account.changeEmailDescription",
            "A verification email will be sent to confirm the change",
          )}
        />
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="current-email" className={typeMetaClass}>
              {t("settings.account.currentEmail", "Current Email")}
            </Label>
            <Input
              id="current-email"
              value={email}
              disabled
              className="h-10 rounded-lg bg-stone-50 text-sm text-stone-500 dark:bg-stone-800/50 dark:text-stone-400"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-email" className={typeMetaClass}>
                {t("settings.account.newEmail", "New Email")}
              </Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t("settings.account.newEmailPlaceholder", "New email address")}
                className="h-10 rounded-lg text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-email" className={typeMetaClass}>
                {t("settings.account.confirmEmail", "Confirm Email")}
              </Label>
              <Input
                id="confirm-email"
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={t("settings.account.confirmEmailPlaceholder", "Confirm new email")}
                className="h-10 rounded-lg text-sm"
              />
            </div>
          </div>

          <SecretField
            id="email-change-password"
            label={t("settings.account.passwordForConfirmation", "Enter your password to confirm")}
            value={emailPassword}
            onChange={setEmailPassword}
            placeholder={t("settings.account.currentPasswordPlaceholder", "Enter current password")}
            autoComplete="current-password"
          />
        </div>

        <div className="flex justify-end">
          <Button
            onClick={submitEmail}
            disabled={isChangingEmail}
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 rounded-lg text-xs"
          >
            {isChangingEmail && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {isChangingEmail
              ? t("settings.account.changing", "Changing…")
              : t("settings.account.updateEmail", "Update Email")}
          </Button>
        </div>
      </div>

      <div className={cn(slateCardClass, "space-y-4 p-4 sm:p-5")}>
        <CardHeading
          icon={Lock}
          title={t("settings.account.changePassword", "Change Password")}
          description={t(
            "settings.account.changePasswordDescription",
            "Keep your account secure with a strong password",
          )}
        />

        <div className="space-y-3">
          <SecretField
            id="current-password"
            label={t("settings.account.currentPassword", "Current Password")}
            value={currentPassword}
            onChange={setCurrentPassword}
            placeholder={t("settings.account.currentPasswordPlaceholder", "Enter current password")}
            autoComplete="current-password"
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SecretField
              id="new-password"
              label={t("settings.account.newPassword", "New Password")}
              value={nextPassword}
              onChange={setNextPassword}
              placeholder={t("settings.account.newPasswordPlaceholder", "New password")}
              autoComplete="new-password"
            />
            <SecretField
              id="confirm-password"
              label={t("settings.account.confirmPassword", "Confirm Password")}
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder={t("settings.account.confirmPasswordPlaceholder", "Confirm password")}
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={submitPassword}
            disabled={isChangingPassword}
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 rounded-lg text-xs"
          >
            {isChangingPassword && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            )}
            {isChangingPassword
              ? t("settings.account.changing", "Changing…")
              : t("settings.account.updatePassword", "Update Password")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsSection;